import {
  Button, Card, ChildAvatar, DataTable, EmptyState, Notice, Pager, Permissions, Row, Select, StatusBadge, ageInMonths, formatAge,
  usePermission, useApi, useClampedPage, useI18n, type ChildSummaryResponse, type Column,
} from '@daghfal/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/** The child roll, filterable by class (FR-ADM-03). */
export function Children() {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const navigate = useNavigate();
  const canCreate = usePermission(Permissions.children.create);
  const canUpdate = usePermission(Permissions.children.update);

  const [search, setSearch] = useState('');
  const [classId, setClassId] = useState('');
  const [page, setPage] = useState(1);

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active' }],
    queryFn: () => api.classes.list({ status: 'Active' }),
    staleTime: 5 * 60_000,
  });

  const children = useQuery({
    queryKey: ['children', { search, classId, page }],
    queryFn: () => api.children.list({
      search,
      ...(classId ? { classId: Number(classId) } : {}),
      page,
      pageSize: 50,
    }),
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, children.data?.totalPages);

  // Only the count: the list itself is its own screen (Q9).
  const gaps = useQuery({
    queryKey: ['registration-gaps', { count: true }],
    queryFn: () => api.children.registrationGaps({ pageSize: 1 }),
    enabled: canUpdate,
  });

  // The same for the consents nobody has answered: until individual-photos consent is recorded, a child is
  // not photographed at all (Q45), so the count belongs where the administration starts its day.
  const consentGaps = useQuery({
    queryKey: ['consent-gaps', { count: true }],
    queryFn: () => api.children.consentGaps({ pageSize: 1 }),
    enabled: canUpdate,
  });

  const columns: Column<ChildSummaryResponse>[] = [
    {
      key: 'name',
      header: t('children.column.name'),
      render: (child) => (
        <Row>
          {/* The allergies are on the roll itself, on the photo, not only on the child's own page: whoever is scanning
              this list is often the person about to hand out food (FR-STF-07, Q75, Q76). */}
          <ChildAvatar child={child} photoUrl={child.photoUrl} size="sm" allergies={child.allergyMarks} />
          <Link className="dg-row-button" to={`/children/${child.id}`}>
            {child.preferredName
              ? `${child.preferredName} ${child.lastName}`
              : `${child.firstName} ${child.lastName}`}
          </Link>
        </Row>
      ),
    },
    {
      key: 'class',
      header: t('children.column.class'),
      render: (child) => child.className ?? <span className="dg-card__hint">{t('children.unassigned')}</span>,
    },
    { key: 'dob', header: t('children.column.dob'), render: (child) => formatDate(child.dateOfBirth) },
    { key: 'age', header: t('children.column.age'), render: (child) => <Age dateOfBirth={child.dateOfBirth} /> },
    { key: 'gender', header: t('children.column.gender'), render: (child) => t(`gender.${child.gender}`) },
    { key: 'status', header: t('children.column.status'), render: (child) => <StatusBadge status={child.status} /> },
  ];

  return (
    <>
      <div className="dg-page-header">
        <div className="dg-page-header__row">
          <h1>{t('children.title')}</h1>
          {canCreate && (
            <Row>
              <Button variant="secondary" onClick={() => navigate('/children/import')}>{t('import.title')}</Button>
              <Button onClick={() => navigate('/children/new')} icon="＋">{t('children.new')}</Button>
            </Row>
          )}
        </div>
        <p className="dg-page-header__lede">{t('children.lede')}</p>
      </div>

      {gaps.data && gaps.data.totalCount > 0 && (
        <Notice tone="warning">
          <Row between>
            <span>{t('registrationGaps.notice', { count: gaps.data.totalCount })}</span>
            <Link className="dg-button dg-button--secondary" to="/children/registration-gaps">
              {t('registrationGaps.open')}
            </Link>
          </Row>
        </Notice>
      )}

      {consentGaps.data && consentGaps.data.totalCount > 0 && (
        <Notice tone="warning">
          <Row between>
            <span>{t('consentGaps.notice', { count: consentGaps.data.totalCount })}</span>
            <Link className="dg-button dg-button--secondary" to="/children/consent-gaps">
              {t('consentGaps.open')}
            </Link>
          </Row>
        </Notice>
      )}

      <Card>
        <Row>
          <label className="dg-field" style={{ flex: '1 1 260px' }}>
            <span className="dg-visually-hidden">{t('children.search')}</span>
            <input
              className="dg-input"
              type="search"
              placeholder={t('children.search')}
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            />
          </label>

          <label className="dg-field">
            <span className="dg-visually-hidden">{t('children.column.class')}</span>
            <Select
              value={classId}
              onChange={(event) => { setClassId(event.target.value); setPage(1); }}
              style={{ inlineSize: 'auto' }}
            >
              <option value="">{t('children.allClasses')}</option>
              {(classes.data?.items ?? []).map((klass) => (
                <option key={klass.id} value={klass.id}>{klass.name}</option>
              ))}
            </Select>
          </label>
        </Row>
      </Card>

      <DataTable
        caption={t('children.caption')}
        columns={columns}
        rows={children.data?.items}
        rowKey={(child) => child.id}
        isLoading={children.isPending}
        error={children.error}
        onRetry={() => void children.refetch()}
        empty={<EmptyState icon="👶" title={t('children.empty')} />}
      />

      <Pager page={page} totalPages={children.data?.totalPages} onPage={setPage} />
    </>
  );
}

/** Years and months — a nursery reads a fourteen-month-old very differently from a two-year-old. */
export function Age({ dateOfBirth }: { dateOfBirth: string }) {
  const i18n = useI18n();
  return <>{formatAge(ageInMonths(dateOfBirth), i18n)}</>;
}
